/**
 * Forms Settings — the pure helpers behind the two sending tabs (New Patients, Medical History Update) and their shared pieces
 * (FormsMessageCard, FormsPreviewPane, CheckPicker). Pure: no Vue, no store, no network.
 * Plan: docs/plans/2026-10-20-forms-settings-plan.md §0 (D4, D6, D7, D13, D15), §1f, §2.1, §2.2.
 *
 * ⛔ THE RULES LIVE IN THE THREE WP0 FILES (./formLink, ./newPatient, ./medHx). This file never re-implements a validation, a block reason, a
 *    default or the rule matcher — it only SHAPES the editor's working copy around them: what the card shows for a stored message, what the
 *    switch card says, what a Save carries. It is NOT one of the three import-free WP0 files (the twin tests do not concatenate it).
 *
 * ⛔ A SAVE NEVER CARRIES THE SWITCH. `newPatientSavePayload` has no `enabled` / `liveSince`; `medHxSavePayload` has no `activated`. The switch card's
 *    own confirm is the only writer of those (D15a); host.js documents the contract.
 *
 * ⛔ THE MEDICAL HISTORY BLOB KEEPS ITS TYPES (form-reminder-common.js reads it): `activated` a JSON boolean, `channels` booleans,
 *    `intervalNumber` a NUMBER (a blank box is '' and is reported as INTERVAL_INVALID), `intervalUnit` 'months' | 'years'.
 */
import {
  isOn, isPlainObject, isFilledText, isValidFormLink, MESSAGE_CHIPS, FORM_LINK_PREVIEW_URL,
} from './formLink';
import { hasTag, withoutReviewRequestTemplates } from '@/utils/wikiTags';
import { NEW_PATIENT_DEFAULTS, NEW_PATIENT_MODES, newRule } from './newPatient';
import {
  MEDHX_DEFAULTS, MEDHX_INTERVAL_UNITS, MEDHX_DEFAULT_INTERVAL, buildMedHxInlineSlot, isInlineMedHxSlot, isWikiSlot,
} from './medHx';

const clone = (v) => JSON.parse(JSON.stringify(v));

// ── the variable chips ───────────────────────────────────────────────────────────────────────────────────────────────────────
/** The chips under a box, as MessageTemplateBox wants them: names WITHOUT braces (the Form link chip is the box's own `link` prop). */
export const CHIP_NAMES = Object.freeze(MESSAGE_CHIPS.map((chip) => chip.token.replace(/^\{\{|\}\}$/g, '')));

// ── forms + packets → the Form link picker ───────────────────────────────────────────────────────────────────────────────────
const FORM_TYPE_TAGS = Object.freeze({ NP_FORM: 'New patient', MED_HX_UPDATE: 'Medical history' });

/** NP_FORM → "New patient", MED_HX_UPDATE → "Medical history", anything else (OTHER, …) → "Other". */
export function formTypeTag(formType) {
  return FORM_TYPE_TAGS[formType] || 'Other';
}

/** "4 forms" / "1 form" for a packet that lists its forms; '' when it does not. */
export function packetMeta(packet) {
  const n = isPlainObject(packet) && Array.isArray(packet.formIDs) ? packet.formIDs.length : -1;
  if (n < 0) return '';
  return `${n} ${n === 1 ? 'form' : 'forms'}`;
}

/** The context's forms[] / packets[] → the box's `link.items` (Packets first, each with "N forms"; forms with their type tag). */
export function buildLinkItems(forms, packets) {
  const items = [];
  (Array.isArray(packets) ? packets : []).forEach((p) => {
    if (!isPlainObject(p) || !isFilledText(String(p.packetID === undefined || p.packetID === null ? '' : p.packetID))) return;
    items.push({ kind: 'packet', id: String(p.packetID), name: typeof p.name === 'string' ? p.name : '', meta: packetMeta(p) });
  });
  (Array.isArray(forms) ? forms : []).forEach((f) => {
    if (!isPlainObject(f) || !isFilledText(String(f.formIDForWeb === undefined || f.formIDForWeb === null ? '' : f.formIDForWeb))) return;
    items.push({ kind: 'form', id: String(f.formIDForWeb), name: typeof f.name === 'string' ? f.name : '', tag: formTypeTag(f.formType) });
  });
  return items;
}

/** The choice as the (loaded) lists name it now; null when the id is in neither list. */
function liveLinkTarget(formLink, forms, packets) {
  if (!isValidFormLink(formLink)) return null;
  if (formLink.kind === 'packet') return (Array.isArray(packets) ? packets : []).find((p) => isPlainObject(p) && String(p.packetID) === String(formLink.id)) || null;
  return (Array.isArray(forms) ? forms : []).find((f) => isPlainObject(f) && String(f.formIDForWeb) === String(formLink.id)) || null;
}

/**
 * What the email preview's link card says: the picked form's name, a packet's "<name> — N forms"; "No form picked yet" for nothing picked.
 * A choice the lists no longer know keeps the name stored with it.
 * @returns {{ picked:boolean, title:string }}
 */
export function describeLinkChoice(formLink, forms, packets) {
  if (!isValidFormLink(formLink)) return { picked: false, title: 'No form picked yet' };
  const live = liveLinkTarget(formLink, forms, packets);
  const name = (live && typeof live.name === 'string' && live.name) || formLink.name || 'Your form';
  if (formLink.kind === 'packet') {
    const meta = live ? packetMeta(live) : '';
    return { picked: true, title: meta ? `${name} — ${meta}` : name };
  }
  return { picked: true, title: name };
}

/** For a packet link: the FormIDForWeb of every form in THAT packet (what linkRestartsClock wants); [] for a form link or an unknown packet. */
export function packetFormIdsFor(formLink, packets) {
  if (!isValidFormLink(formLink) || formLink.kind !== 'packet') return [];
  const hit = liveLinkTarget(formLink, [], packets);
  return hit && Array.isArray(hit.formIDs) ? hit.formIDs.map(String) : [];
}

// ── the preview's sample patient ─────────────────────────────────────────────────────────────────────────────────────────────
const WEEKDAYS = Object.freeze(['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']);
const MONTHS = Object.freeze(['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']);
const pad2 = (n) => (n < 10 ? `0${n}` : String(n));

/**
 * The sample appointment: a week from `now` at 10:00 AM. `aptDate` is what {{apt_date}} really reads like in a sent message ("MM-DD-YYYY hh:mm A",
 * the exe's format — docs plan §1h); `label` is the friendly line under "Preview" ("Tue, Oct 27 at 10:00 AM").
 */
export function sampleAppointment(now) {
  const base = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
  const d = new Date(base.getFullYear(), base.getMonth(), base.getDate() + 7);
  return {
    aptDate: `${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}-${d.getFullYear()} 10:00 AM`,
    label: `${WEEKDAYS[d.getDay()]}, ${MONTHS[d.getMonth()]} ${d.getDate()} at 10:00 AM`,
  };
}

/** The extra sample values the post-op preview does not know: the appointment and the masked form link. */
export function previewExtraVars(sample) {
  return { apt_date: sample.aptDate, form_link: FORM_LINK_PREVIEW_URL };
}

// ── the switch card ──────────────────────────────────────────────────────────────────────────────────────────────────────────
/** Reasons the SERVER decides and that do not change while the office edits (kept from the context, never recomputed). */
export const NEW_PATIENT_STICKY_REASON_CODES = Object.freeze(['WORKFLOW_CHECK_FAILED', 'WORKFLOW_SENDS', 'CLINICS_NOT_SUPPORTED', 'CLINICS_CHECK_FAILED']);

/** Reason codes WP0's medHxBlockReasons knows; a server reason with any other code is shown after them. */
const MEDHX_KNOWN_REASON_CODES = Object.freeze(['MESSAGE_PROBLEMS', 'INTERVAL_INVALID', 'NO_TRIGGER_FORMS', 'SMS_NOT_READY', 'EMAIL_NOT_READY', 'NOTHING_ON']);

function usableReasons(list) {
  return (Array.isArray(list) ? list : []).filter((r) => isPlainObject(r) && typeof r.code === 'string' && isFilledText(r.message));
}

/**
 * New Patients: the server's sticky reasons first (server-only codes + WORKFLOW_SENDS, in the server's order), then the live recompute
 * (WP0 newPatientBlockReasons over the working copy) minus any code the server already gave. The FIRST entry is the one the card shows.
 */
export function newPatientSwitchReasons(serverReasons, liveReasons) {
  const sticky = usableReasons(serverReasons).filter((r) => NEW_PATIENT_STICKY_REASON_CODES.indexOf(r.code) !== -1);
  const rest = usableReasons(liveReasons).filter((r) => !sticky.some((s) => s.code === r.code));
  return sticky.map((r) => ({ code: r.code, message: r.message })).concat(rest.map((r) => ({ code: r.code, message: r.message })));
}

/** Medical History: WP0's live reasons, then any server reason whose code WP0 does not know (none today). */
export function medHxSwitchReasons(serverReasons, liveReasons) {
  const live = usableReasons(liveReasons).map((r) => ({ code: r.code, message: r.message }));
  const extra = usableReasons(serverReasons)
    .filter((r) => MEDHX_KNOWN_REASON_CODES.indexOf(r.code) === -1 && !live.some((l) => l.code === r.code))
    .map((r) => ({ code: r.code, message: r.message }));
  return live.concat(extra);
}

/**
 * The switch card's view.
 *   paused   ON, and the FIRST reason is one the sender yields to at send time (`paused.codes`, e.g. WORKFLOW_SENDS: an active
 *            workflow already sends new patients their forms, so the trigger skips every booking — D15c). It is still ON and resumes
 *            by itself when that reason goes away, so the card says "paused", never a lock on a green switch. Can be switched OFF.
 *   blocked  the FIRST reason, locked — except while ON, when it can always be switched OFF
 *   on       "<onMessage with the server's liveSince>"
 *   off      the tab's own "Off — …" line
 * @returns {{ kind:'off'|'on'|'paused'|'blocked', checked:boolean, canToggle:boolean, message:string }}
 */
export function sendSwitchState({ enabled, liveSinceText, reasons, offMessage, onMessage, onMessageNoDate, paused } = {}) {
  const list = usableReasons(reasons);
  if (list.length > 0 && enabled && paused && Array.isArray(paused.codes) && paused.codes.indexOf(list[0].code) !== -1
      && typeof paused.message === 'string' && paused.message) {
    return { kind: 'paused', checked: true, canToggle: true, message: paused.message };
  }
  if (list.length > 0) return { kind: 'blocked', checked: !!enabled, canToggle: !!enabled, message: list[0].message };
  if (enabled) {
    return {
      kind: 'on',
      checked: true,
      canToggle: true,
      message: liveSinceText ? onMessage.replace('{since}', liveSinceText) : onMessageNoDate,
    };
  }
  return { kind: 'off', checked: false, canToggle: true, message: offMessage };
}

/** "Text" / "Email" / "Text and Email" / '' — the confirm dialog's subject. */
export function channelWords(smsOn, emailOn) {
  if (smsOn && emailOn) return 'Text and Email';
  if (smsOn) return 'Text';
  if (emailOn) return 'Email';
  return '';
}

// ── a message card ↔ a stored message ────────────────────────────────────────────────────────────────────────────────────────
function cleanFormLink(v) {
  return isValidFormLink(v) ? { kind: v.kind, id: v.id, name: v.name } : null;
}

function strList(list) {
  if (!Array.isArray(list)) return [];
  return list
    .filter((x) => typeof x === 'string' || typeof x === 'number')
    .map((x) => String(x).trim())
    .filter((x) => x !== '');
}

/** What a FormsMessageCard shows / emits: `on` is a Boolean there; the stored blobs say 'TRUE' / 'FALSE'. */
export function toCardMessage(msg, channel) {
  const m = isPlainObject(msg) ? msg : {};
  const out = { on: isOn(m.on), body: typeof m.body === 'string' ? m.body : '', formLink: cleanFormLink(m.formLink) };
  if (channel === 'email') out.subject = typeof m.subject === 'string' ? m.subject : '';
  return out;
}

/** The New Patients stored shape (key order is the server's: on, subject, body, formLink). */
export function fromCardMessage(card, channel) {
  const c = isPlainObject(card) ? card : {};
  const out = { on: c.on === true || isOn(c.on) ? 'TRUE' : 'FALSE' };
  if (channel === 'email') out.subject = typeof c.subject === 'string' ? c.subject : '';
  out.body = typeof c.body === 'string' ? c.body : '';
  out.formLink = cleanFormLink(c.formLink);
  return out;
}

function storedMessage(raw, channel, fallback) {
  if (!isPlainObject(raw)) return fromCardMessage(toCardMessage(fallback, channel), channel);
  const out = { on: isOn(raw.on) ? 'TRUE' : 'FALSE' };
  if (channel === 'email') out.subject = typeof raw.subject === 'string' ? raw.subject : fallback.subject;
  out.body = typeof raw.body === 'string' ? raw.body : fallback.body;
  out.formLink = cleanFormLink(raw.formLink);
  return out;
}

// ── New Patients ─────────────────────────────────────────────────────────────────────────────────────────────────────────────
/**
 * The editable part of NEW_PATIENT_FORMS_SETTINGS — everything EXCEPT `enabled` / `liveSince` (and the seeder's `review` flags, which the editor
 * neither shows nor sends). Absent / partial settings fall back to the WP0 defaults, so a never-set-up office starts from them. A fresh mutable
 * object every call; idempotent (newPatientEditable(newPatientEditable(x)) equals the first).
 */
export function newPatientEditable(settings) {
  const s = isPlainObject(settings) ? settings : {};
  const everyone = isPlainObject(s.everyone) ? s.everyone : {};
  const fresh = newRule('np-0');
  return {
    mode: NEW_PATIENT_MODES.indexOf(s.mode) !== -1 ? s.mode : NEW_PATIENT_DEFAULTS.mode,
    everyone: {
      sms: storedMessage(everyone.sms, 'sms', NEW_PATIENT_DEFAULTS.everyone.sms),
      email: storedMessage(everyone.email, 'email', NEW_PATIENT_DEFAULTS.everyone.email),
    },
    rules: (Array.isArray(s.rules) ? s.rules : []).filter(isPlainObject).map((r, i) => ({
      id: isFilledText(r.id) ? r.id : `np-${i + 1}`,
      label: typeof r.label === 'string' ? r.label : '',
      codes: strList(r.codes),
      providerNums: strList(r.providerNums),
      sms: storedMessage(r.sms, 'sms', fresh.sms),
      email: storedMessage(r.email, 'email', fresh.email),
    })),
  };
}

/** The blob the WP0 validators / block reasons read: the working copy with the switch the server holds put back on. */
export function newPatientBlob(editable, enabled) {
  return Object.assign(clone(editable), { enabled: enabled === 'TRUE' ? 'TRUE' : 'FALSE' });
}

/** What Save sends for the New Patients section. ⛔ never `enabled` / `liveSince`. */
export function newPatientSavePayload(editable) {
  const e = newPatientEditable(editable);
  return { mode: e.mode, everyone: e.everyone, rules: e.rules };
}

export function newPatientSignature(editable) {
  return JSON.stringify(newPatientEditable(editable));
}

/** A rule id no current rule uses: np-<largest number in use + 1>. */
export function nextRuleId(rules) {
  let max = 0;
  (Array.isArray(rules) ? rules : []).forEach((r) => {
    const m = /^np-(\d+)$/.exec(r && typeof r.id === 'string' ? r.id : '');
    if (m) max = Math.max(max, Number(m[1]));
  });
  return `np-${max + 1}`;
}

/** The provider multiselect's options from customer.providers: hidden ones dropped, ONE entry per ProvNum (the bootstrap join fans a provider out per employee). */
export function providerOptions(providers) {
  const seen = new Set();
  const out = [];
  (Array.isArray(providers) ? providers : []).forEach((p) => {
    if (!isPlainObject(p) || p.IsHidden || p.ProvNum === undefined || p.ProvNum === null) return;
    const value = String(p.ProvNum);
    if (seen.has(value)) return;
    seen.add(value);
    const name = `${p.FName || ''} ${p.LName || ''}`.trim() || p.Abbr || `Provider ${value}`;
    out.push({ value, name });
  });
  return out.sort((a, b) => a.name.localeCompare(b.name));
}

/** The collapsed rule's row: "<label> · <codes> · <providers> · Text + Email". */
export function ruleSummary(rule, { labelForCode, providerNameFor } = {}) {
  const r = isPlainObject(rule) ? rule : {};
  const codes = strList(r.codes).map((c) => (typeof labelForCode === 'function' ? labelForCode(c) : c));
  const providers = strList(r.providerNums).map((p) => (typeof providerNameFor === 'function' ? providerNameFor(p) : p));
  const channels = [isPlainObject(r.sms) && isOn(r.sms.on) ? 'Text' : '', isPlainObject(r.email) && isOn(r.email.on) ? 'Email' : ''].filter(Boolean);
  return {
    label: isFilledText(r.label) ? r.label.trim() : 'Untitled rule',
    codes: codes.length ? codes.join(', ') : 'No codes yet',
    providers: providers.length ? providers.join(', ') : 'Any provider',
    channels: channels.length ? channels.join(' + ') : 'Not sending',
  };
}

// ── Medical History Update ───────────────────────────────────────────────────────────────────────────────────────────────────
function medHxInterval(raw, settingsPresent) {
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  if (typeof raw === 'string' && /^\s*\d+\s*$/.test(raw)) return Number(raw.trim());
  return settingsPresent ? '' : MEDHX_DEFAULT_INTERVAL.intervalNumber;
}

function cleanTriggerForms(list) {
  const seen = new Set();
  const out = [];
  (Array.isArray(list) ? list : []).forEach((f) => {
    if (!isPlainObject(f) || !isFilledText(f.FormIDForWeb) || seen.has(f.FormIDForWeb)) return;
    seen.add(f.FormIDForWeb);
    out.push({ FormIDForWeb: f.FormIDForWeb, Name: typeof f.Name === 'string' ? f.Name : '' });
  });
  return out;
}

/** One channel's slot state: which kind it is, plus BOTH kinds kept in memory so "Use a template instead" / "Switch to a plain message" lose nothing. */
function slotState(slot, channel) {
  const inline = isInlineMedHxSlot(slot)
    ? buildMedHxInlineSlot(channel, {
      text: isPlainObject(slot.inline) ? slot.inline.text : undefined,
      subject: isPlainObject(slot.inline) ? slot.inline.subject : undefined,
      formLink: isPlainObject(slot.inline) ? cleanFormLink(slot.inline.formLink) : null,
    })
    : buildMedHxInlineSlot(channel);
  if (isWikiSlot(slot)) return { mode: 'page', inline, page: clone(slot) };
  return { mode: 'inline', inline, page: null };
}

/**
 * Settings (the stored FORMS_UPDATE_REMINDER_SETTINGS blob, or null for an office that never set it up) → the editor's working state:
 *   work        { intervalNumber, intervalUnit, channels:{sms,email}, triggerForms }
 *   slots       { sms, email } each { mode:'inline'|'page', inline:<inline slot>, page:<the saved-page (wiki) slot>|null }
 *   activated   the stored switch (a JSON boolean)
 * `null` starts from the WP0 defaults (12 months, both channels on with the default plain messages, no trigger forms).
 */
export function medHxLoad(settings) {
  const present = isPlainObject(settings);
  const src = present ? settings : MEDHX_DEFAULTS;
  const channels = isPlainObject(src.channels) ? src.channels : {};
  return {
    work: {
      intervalNumber: medHxInterval(src.intervalNumber, present),
      intervalUnit: MEDHX_INTERVAL_UNITS.indexOf(src.intervalUnit) !== -1 ? src.intervalUnit : MEDHX_DEFAULT_INTERVAL.intervalUnit,
      channels: { sms: channels.sms === true, email: channels.email === true },
      triggerForms: cleanTriggerForms(src.triggerForms),
    },
    slots: { sms: slotState(src.smsTemplate, 'sms'), email: slotState(src.emailTemplate, 'email') },
    activated: present && settings.activated === true,
  };
}

/** The slot that is actually stored for a channel right now: the wiki page (or null while none is picked) or the plain message. */
export function effectiveSlot(state) {
  if (!state) return null;
  if (state.mode === 'page') return state.page ? clone(state.page) : null;
  return clone(state.inline);
}

/** The blob the WP0 validators / block reasons read: the working copy + the switch as the server holds it. */
export function medHxBlob(model, activated) {
  return {
    activated: activated === true,
    intervalNumber: model.work.intervalNumber,
    intervalUnit: model.work.intervalUnit,
    channels: clone(model.work.channels),
    smsTemplate: effectiveSlot(model.slots.sms),
    emailTemplate: effectiveSlot(model.slots.email),
    triggerForms: clone(model.work.triggerForms),
  };
}

/** What Save sends for the Medical History section. ⛔ never `activated`. */
export function medHxSavePayload(model) {
  const blob = medHxBlob(model, false);
  delete blob.activated;
  return blob;
}

export function medHxSignature(model) {
  return JSON.stringify(medHxSavePayload(model));
}

/** An inline slot rebuilt around new text — the shape a card edit produces. */
export function inlineSlotFrom(channel, card) {
  const c = isPlainObject(card) ? card : {};
  return buildMedHxInlineSlot(channel, {
    text: typeof c.body === 'string' ? c.body : '',
    subject: channel === 'email' ? (typeof c.subject === 'string' ? c.subject : '') : undefined,
    formLink: cleanFormLink(c.formLink),
  });
}

/** A card's view of an inline slot. */
export function cardFromInlineSlot(slot, channel, on) {
  const inline = isPlainObject(slot) && isPlainObject(slot.inline) ? slot.inline : {};
  const out = { on: on === true, body: typeof inline.text === 'string' ? inline.text : '', formLink: cleanFormLink(inline.formLink) };
  if (channel === 'email') out.subject = typeof inline.subject === 'string' ? inline.subject : '';
  return out;
}

/**
 * The saved pages a Medical History slot may pick — what the classic modal lists: Text = tags form + sms-template; Email = form + email-template +
 * a SendGrid template (exact tag match, never a substring: 'form' is not 'platform'). Review-request pages never appear in a general picker
 * (cypress/node-tests/template-variable-validation.test.js "PICKERS").
 */
export function medHxWikiPages(pages, channel) {
  const list = withoutReviewRequestTemplates((Array.isArray(pages) ? pages : []).filter(isPlainObject));
  return list.filter((p) => (channel === 'email'
    ? hasTag(p, 'form') && hasTag(p, 'email-template') && !!p.SendgridTemplateID
    : hasTag(p, 'form') && hasTag(p, 'sms-template')));
}
